import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner, Alert, Tab, Nav } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { shiftAPI } from '../../services/shift';
import { getErrorMessage } from '../../utils/apiClient';
import { taskAPI } from '../../services/task';
import { toast } from 'react-toastify';
import ShiftHeader from '../../components/admin-shift-review/ShiftHeader';
import TasksTab from '../../components/admin-shift-review/TasksTab';
import OrdersTab from '../../components/admin-shift-review/OrdersTab';
import ExpensesTab from '../../components/admin-shift-review/ExpensesTab';
import CashReportTab from '../../components/admin-shift-review/CashReportTab';

const AdminShiftReviewPage = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // #171726 — link sâu từ tin "Task đã hoàn thành" (nhóm Báo Cáo) mở thẳng đúng dòng task.
  const focusTaskId = searchParams.get('task');

  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('tasks');
  const [approving, setApproving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!shiftId) return;

    setLoading(true);
    try {
      const data = await shiftAPI.getShiftReviewDetail(shiftId);
      setReviewData(data);
    } catch (err) {
      console.error('Error fetching shift review data', err);
      setError(getErrorMessage(err) || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      const userId = JSON.parse(localStorage.getItem('user'))?.id;
      await shiftAPI.approveShift(shiftId, userId);
      toast.success('Đã duyệt ca thành công');
      fetchData();
    } catch (err) {
      // #190823 — KHÔNG toast chữ cứng: backend đã trả lý do rõ ràng (vd "Đang trong tháng đầu
      // tập việc, chỉ nhận được ca gãy..."), nuốt mất thì quản lý không biết vì sao bị chặn.
      // Lưu ý: `apiClient` đã chuẩn hoá lỗi thành `{ message, code, ... }` — KHÔNG có
      // `err.response`, nên phải đi qua `getErrorMessage`, không đọc `err.response.data.message`.
      toast.error(getErrorMessage(err) || 'Duyệt ca thất bại');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setApproving(true);
    try {
      const userId = JSON.parse(localStorage.getItem('user'))?.id;
      await shiftAPI.rejectShift(shiftId, userId);
      toast.success('Đã từ chối ca');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Từ chối ca thất bại');
    } finally {
      setApproving(false);
    }
  };

  const handleTaskReview = async (taskId, action, reviewNote) => {
    try {
      if (action === 'ADMIN_ARBITRATE') {
        await taskAPI.adminReviewShiftTask(taskId, {
          decision: 'CUSTOM',
          admin_notes: reviewNote,
        });
      } else {
        const result = action === 'APPROVE' ? 'APPROVED' : 'REJECTED_AND_FIXED';
        await taskAPI.reviewShiftTask(taskId, {
          review_result: result,
          review_notes: reviewNote,
          review_evidence_urls: [],
        });
      }
      toast.success('Đã đánh giá thành công');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Đánh giá thất bại');
      throw err;
    }
  };

  // #171726 — Quản lý yêu cầu nhân viên làm lại/chụp lại task đã hoàn thành.
  const handleTaskRedo = async (taskId, reason) => {
    try {
      await taskAPI.requestRedoShiftTask(taskId, { reason });
      toast.success('Đã yêu cầu nhân viên làm lại');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Yêu cầu làm lại thất bại');
      throw err;
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className='mc-page'>
          <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '60vh' }}>
            <Spinner animation='border' variant='primary' />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !reviewData) {
    return (
      <PageLayout>
        <div className='mc-page'>
          <div className='mc-page-section'>
            <Alert variant='danger'>{error || 'Không tìm thấy ca làm việc'}</Alert>
            <Button variant='outline-primary' onClick={() => navigate('/shift-review')}>Quay lại</Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const { shift, tasks, task_summary, orders, order_summary, cash_vouchers, voucher_summary, reconciliation } = reviewData;
  const canApprove = shift.status === 'COMPLETED' || shift.status === 'COMPLETED_WAIT_APPROVE';

  return (
    <PageLayout>
      <div className='mc-page'>
        {/* Page Header */}
        <div className='d-flex align-items-center mb-4'>
          <Button variant='outline-secondary' size='sm' className='me-3' onClick={() => navigate('/shift-review')}>
            <i className='icofont-arrow-left me-1'></i> Quay lại
          </Button>
        </div>

        {/* Shift Info Card + Mini Summary */}
        <ShiftHeader
          shift={shift}
          taskSummary={task_summary}
          orderSummary={order_summary}
          voucherSummary={voucher_summary}
          reconciliation={reconciliation}
        />

        {/* Tabs */}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Nav variant='tabs' className='mb-3'>
            <Nav.Item>
              <Nav.Link eventKey='tasks'>
                <i className='icofont-tasks-alt me-1'></i>
                Công việc
                <span className='badge bg-primary ms-2'>{task_summary?.total || 0}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='orders'>
                <i className='icofont-cart me-1'></i>
                Đơn hàng
                <span className='badge bg-secondary ms-2'>{order_summary?.total_orders || 0}</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='expenses'>
                <i className='icofont-bill me-1'></i>
                Phiếu chi
                <span className='badge bg-secondary ms-2'>{voucher_summary?.total_vouchers || 0}</span>
              </Nav.Link>
            </Nav.Item>
            {shift.is_cashier && (
              <Nav.Item>
                <Nav.Link eventKey='cash'>
                  <i className='icofont-money me-1'></i>
                  Báo cáo tiền mặt
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey='tasks'>
              <TasksTab
                tasks={tasks}
                taskSummary={task_summary}
                onTaskReview={handleTaskReview}
                onTaskRedo={handleTaskRedo}
                focusTaskId={focusTaskId}
              />
            </Tab.Pane>
            <Tab.Pane eventKey='orders'>
              <OrdersTab orders={orders} orderSummary={order_summary} />
            </Tab.Pane>
            <Tab.Pane eventKey='expenses'>
              <ExpensesTab vouchers={cash_vouchers} voucherSummary={voucher_summary} />
            </Tab.Pane>
            {shift.is_cashier && (
              <Tab.Pane eventKey='cash'>
                <CashReportTab reconciliation={reconciliation} shift={shift} />
              </Tab.Pane>
            )}
          </Tab.Content>
        </Tab.Container>
      </div>
    </PageLayout>
  );
};

export default AdminShiftReviewPage;
